import "@/App.css";
import "./i18n";
import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppConfigProvider } from './context/AppConfigContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from 'sonner';
import { Landing } from './pages/Landing';
import NotFound from './pages/NotFound';
import { AuthCallback } from './pages/AuthCallback';
import { Quiz } from './pages/Quiz';
import { Home } from './pages/Home';
import { BetaGate } from './components/BetaGate';

// v4.3 — route-level code-splitting. Secondary/rarely-first-visit routes are
// lazy-loaded so the initial bundle only carries the landing/auth/home path.
const ProfilePage = lazy(() => import('./pages/Profile').then(m => ({ default: m.ProfilePage })));
const SocialPage = lazy(() => import('./pages/Social').then(m => ({ default: m.SocialPage })));
const DiscoveryPage = lazy(() => import('./pages/Discovery'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const ProDetail = lazy(() => import('./pages/ProDetail').then(m => ({ default: m.ProDetail })));
const ProRequest = lazy(() => import('./pages/ProDetail').then(m => ({ default: m.ProRequest })));
const SharingPage = lazy(() => import('./pages/Sharing').then(m => ({ default: m.SharingPage })));
const SharedConcept = lazy(() => import('./pages/SharedConcept'));
const OnboardingMode = lazy(() => import('./pages/OnboardingMode'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const SharedPost = lazy(() => import('./pages/SharedPost'));
const StyleMeComposerPage = lazy(() => import('./pages/StyleMe').then(m => ({ default: m.StyleMeComposerPage })));
const EventsListPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventsListPage })));
const EventDetailPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventDetailPage })));
const EventInviteAcceptPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventInviteAcceptPage })));
const BetaAccessRedeem = lazy(() => import('./pages/BetaAccess').then(m => ({ default: m.BetaAccessRedeem })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ClaimHandle = lazy(() => import('./pages/ClaimHandle'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));
const SettingsPrivacy = lazy(() => import('./pages/SettingsPrivacy'));
const SettingsData = lazy(() => import('./pages/SettingsData'));
const StylePreferences = lazy(() => import('./pages/StylePreferences'));
const Closet = lazy(() => import('./pages/Closet').then(m => ({ default: m.Closet })));
const Shop = lazy(() => import('./pages/Shop').then(m => ({ default: m.Shop })));
const FitCheckPage = lazy(() => import('./pages/FitCheck').then(m => ({ default: m.FitCheckPage })));
const MemoryPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.MemoryPage })));
const WishlistPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.WishlistPage })));
const BoardsPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.BoardsPage })));
const BoardDetailPage = lazy(() => import('./pages/BoardDetail').then(m => ({ default: m.BoardDetailPage })));
const SharedBoardPage = lazy(() => import('./pages/BoardDetail').then(m => ({ default: m.SharedBoardPage })));
const SettingsPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.SettingsPage })));
const OutfitBuilderPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.OutfitBuilderPage })));
const StylistPage = lazy(() => import('./pages/AppPages').then(m => ({ default: m.StylistPage })));
const StylistIntentPage = lazy(() => import('./pages/StylistIntent').then(m => ({ default: m.StylistIntentPage })));
const PrivacyPolicy = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfUse = lazy(() => import('./pages/Legal').then(m => ({ default: m.TermsOfUse })));
const ImageCredits = lazy(() => import('./pages/Legal').then(m => ({ default: m.ImageCredits })));
const DataDeletion = lazy(() => import('./pages/Legal').then(m => ({ default: m.DataDeletion })));
const AiDisclosure = lazy(() => import('./pages/Legal').then(m => ({ default: m.AiDisclosure })));
const CommunityGuidelines = lazy(() => import('./pages/Legal').then(m => ({ default: m.CommunityGuidelines })));
const BetaNotice = lazy(() => import('./pages/Legal').then(m => ({ default: m.BetaNotice })));
const AffiliateDisclosure = lazy(() => import('./pages/Legal').then(m => ({ default: m.AffiliateDisclosure })));
const LegalGate = lazy(() => import('./pages/legal/LegalGate').then(m => ({ default: m.LegalGate })));
const InviteLandingPage = lazy(() => import('./pages/InviteLanding'));
const PlannerPage = lazy(() => import('./pages/Planner'));
const StyleAcademyPage = lazy(() => import('./pages/StyleAcademy'));
const LegalHistoryPage = lazy(() => import('./pages/legal/LegalHistory'));
const PricingPage = lazy(() => import('./pages/Payments').then(m => ({ default: m.PricingPage })));
const BillingPage = lazy(() => import('./pages/Payments').then(m => ({ default: m.BillingPage })));
const CheckoutSuccessPage = lazy(() => import('./pages/Payments').then(m => ({ default: m.CheckoutSuccessPage })));
const CheckoutCancelPage = lazy(() => import('./pages/Payments').then(m => ({ default: m.CheckoutCancelPage })));
const SearchPage = lazy(() => import('./pages/Search'));
const SocialCompose = lazy(() => import('./pages/SocialCompose'));
const SocialPostDetail = lazy(() => import('./pages/SocialPostDetail'));
const Messages = lazy(() => import('./pages/Messages'));
const MessageThread = lazy(() => import('./pages/MessageThread'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const BlockedUsers = lazy(() => import('./pages/BlockedUsers'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfile').then(m => ({ default: m.PublicProfilePage })));
const NotificationsPage = lazy(() => import('./pages/Notifications').then(m => ({ default: m.NotificationsPage })));
const ProfilePublicPreview = lazy(() => import('./pages/ProfileOwnership').then(m => ({ default: m.ProfilePublicPreview })));
const ProfilePublicEdit = lazy(() => import('./pages/ProfileOwnership').then(m => ({ default: m.ProfilePublicEdit })));
const ProfileFollowers = lazy(() => import('./pages/ProfileOwnership').then(m => ({ default: m.ProfileFollowers })));
const ProfileFollowing = lazy(() => import('./pages/ProfileOwnership').then(m => ({ default: m.ProfileFollowing })));
const ProfileRequests = lazy(() => import('./pages/ProfileOwnership').then(m => ({ default: m.ProfileRequests })));
const AllFeaturesPage = lazy(() => import('./pages/AllFeatures'));
const ProposalsInboxPage = lazy(() => import('./pages/ProposalsInbox'));
import { ErrorBoundary } from './components/ErrorBoundary';
import axios from 'axios';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { useExperienceMode } from './hooks/useExperienceMode';

// Mode-Based UX Phase 1 — smart default landing. Members who picked an
// experience mode land on their chosen surface instead of universal Home.
const ModeAwareLanding = () => {
  const modeFlag = useFeatureFlag('mode_based_experience_v1');
  const { ready, prefs } = useExperienceMode();
  if (!modeFlag.ready || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]">
        <div className="w-8 h-8 border-2 border-[#0D0D0D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (modeFlag.on && prefs?.configured) {
    const landing = prefs.default_landing_surface;
    if (landing && landing !== '/home' && landing !== '/') {
      return <Navigate to={landing} replace />;
    }
  }
  return <Home />;
};

// Founder Console — a fully isolated back-office shell at /founder/*.
// Code-split so testers never download founder tooling.
const FounderConsole = lazy(() => import('./founder/FounderConsoleRouter'));

axios.defaults.withCredentials = true;

// Legacy /admin/* URLs redirect into the new Founder Console namespace.
// Most-specific prefixes listed first.
const ADMIN_REDIRECTS = [
  ['/admin/reference-engine/sources', '/founder/tools/reference-engine/sources'],
  ['/admin/reference-engine', '/founder/reference-engine'],
  ['/admin/reference-data', '/founder/tools/reference-data'],
  ['/admin/founder-circle', '/founder/founder-circle'],
  ['/admin/beta-access', '/founder/tools/beta-access'],
  ['/admin/users', '/founder/crm'],
  ['/admin/safety', '/founder/moderation'],
  ['/admin/payments', '/founder/tools/payments'],
  ['/admin/settings', '/founder/tools/settings'],
  ['/admin/merchants', '/founder/tools/merchants'],
  ['/admin/data-tools', '/founder/tools/data-tools'],
  ['/admin/csv', '/founder/tools/data-tools'],
  ['/admin/evidence', '/founder/tools/data-tools'],
  ['/admin/data-safety', '/founder/tools/data-safety'],
  ['/admin/ai-lab', '/founder/tools/ai-lab'],
  ['/admin/ip-rights', '/founder/tools/ip-readiness'],
  ['/admin/ip-readiness', '/founder/tools/ip-readiness'],
  ['/admin/feedback', '/founder/tools/feedback'],
  ['/admin/assets', '/founder/tools/assets'],
  ['/admin/referrals', '/founder/tools/referrals'],
  ['/admin/collections', '/founder/tools/collections'],
  ['/admin/campaigns', '/founder/tools/campaigns'],
  ['/admin/revenue', '/founder/tools/revenue'],
  ['/admin/style-brain', '/founder/tools/style-brain'],
];

const LegacyAdminRedirect = () => {
  const { pathname } = useLocation();
  const hit = ADMIN_REDIRECTS.find(([from]) => pathname === from || pathname.startsWith(`${from}/`));
  return <Navigate to={hit ? hit[1] : '/founder'} replace />;
};

const RootRoute = () => {
  const { user, loading } = useAuth();
  const [gate, setGate] = useState(null);

  const fetchGate = useCallback(async () => {
    try {
      const res = await axios.get(`${window.__SG_API_BASE__}/api/beta-gate/status`);
      setGate(res.data);
    } catch {
      // v4.3.1 Production P0 — FAIL CLOSED. A failed gate probe must NOT
      // unlock a gated account. Default to gated + surface a retry.
      setGate({ gate_active: true, unlocked: false, _error: true });
    }
  }, []);

  useEffect(() => {
    fetchGate();
  }, [fetchGate]);

  if (loading || !gate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]">
        <div className="w-8 h-8 border-2 border-[#0D0D0D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (gate._error && user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]" data-testid="beta-gate-error">
        <div className="text-center max-w-sm px-6">
          <p className="text-[#0D0D0D] font-light mb-2">We couldn't reach the beta service.</p>
          <button
            data-testid="beta-gate-retry"
            onClick={fetchGate}
            className="mt-4 px-6 py-2 rounded-full bg-[#0D0D0D] text-white text-sm tracking-wide">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (user) {
    const isApproved = (user.role !== 'pending' && user.role !== 'waitlist') || gate.flags?.official_public_launch_v1?.enabled;
    if (!isApproved && gate.gate_active) {
      return <BetaGate onUnlocked={fetchGate} />;
    }
    return <ModeAwareLanding />;
  }

  return <Landing />;
};

const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F1]">
        <div className="w-8 h-8 border-2 border-[#0D0D0D] border-t-transparent rounded-full animate-spin" />
      </div>
    }>
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Landing initialMode="login" />} />
      <Route path="/signup" element={<Landing initialMode="choice" />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      
      <Route path="/beta-gate" element={<BetaGate />} />
      <Route path="/beta-access" element={<BetaAccessRedeem />} />
      {/* v4.1 — PUBLIC share deep links. Anyone can open these, no account. */}
      <Route path="/shared/concept/:cardId" element={<SharedConcept />} />
      <Route path="/shared/post/:postId" element={<SharedPost />} />
      
      <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
      <Route path="/claim-handle" element={<ProtectedRoute skipLegalGate={true}><ClaimHandle /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/shop" element={<Shop />} /> {/* v3.9 — public shop browsing; payment/premium stay gated elsewhere */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      {/* RC1.1.5 — Profile ownership routes (founder-listed). Each renders
          the current logged-in user's data — never a seeded demo profile. */}
      <Route path="/profile/public"      element={<ProtectedRoute><ProfilePublicPreview /></ProtectedRoute>} />
      <Route path="/profile/public/edit" element={<ProtectedRoute><ProfilePublicEdit /></ProtectedRoute>} />
      <Route path="/profile/followers"   element={<ProtectedRoute><ProfileFollowers /></ProtectedRoute>} />
      <Route path="/profile/following"   element={<ProtectedRoute><ProfileFollowing /></ProtectedRoute>} />
      <Route path="/profile/requests"    element={<ProtectedRoute><ProfileRequests /></ProtectedRoute>} />
      <Route path="/notifications"        element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/notifications/settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/profile/blocked-users"  element={<ProtectedRoute><BlockedUsers /></ProtectedRoute>} />
      <Route path="/messages"               element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messages/new/:draft_user_id" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
      <Route path="/messages/:conversation_id" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
      {/* RC1.1.6 — Legacy alias. Older builds linked Manage Social Settings
          to /profile/social which rendered blank. Redirect to canonical edit. */}
      <Route path="/profile/social"      element={<Navigate to="/profile/public/edit" replace />} />
      {/* Closeout P0 — HandleClaimBanner linked /profile/ownership which was
          never registered; route it to the canonical public-profile editor. */}
      <Route path="/profile/ownership"   element={<Navigate to="/profile/public/edit" replace />} />
      <Route path="/closet" element={<ProtectedRoute><Closet /></ProtectedRoute>} />
      <Route path="/outfit-builder" element={<ProtectedRoute><OutfitBuilderPage /></ProtectedRoute>} />
      <Route path="/stylist" element={<ProtectedRoute><StylistPage /></ProtectedRoute>} />
      <Route path="/stylist/action" element={<ProtectedRoute><StylistIntentPage /></ProtectedRoute>} />
      <Route path="/fit-check" element={<ProtectedRoute><FitCheckPage /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><SocialPage /></ProtectedRoute>} />
      <Route path="/discovery" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
      <Route path="/social/new" element={<ProtectedRoute><SocialCompose /></ProtectedRoute>} />
      <Route path="/social/camera" element={<Navigate to="/fit-check" replace />} /> {/* v4.3 — StyleCameraFab target; FitCheck owns the camera capture flow */}
      <Route path="/social/post/:post_id" element={<ProtectedRoute><SocialPostDetail /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
      <Route path="/style-academy" element={<ProtectedRoute><StyleAcademyPage /></ProtectedRoute>} />
      <Route path="/settings/legal" element={<ProtectedRoute><LegalHistoryPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
      <Route path="/pros/:pro_id" element={<ProtectedRoute><ProDetail /></ProtectedRoute>} />
      <Route path="/pros/:pro_id/request" element={<ProtectedRoute><ProRequest /></ProtectedRoute>} />
      <Route path="/memory" element={<ProtectedRoute><MemoryPage /></ProtectedRoute>} />
      <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
      <Route path="/boards" element={<ProtectedRoute><BoardsPage /></ProtectedRoute>} />
      <Route path="/boards/shared/:token" element={<SharedBoardPage />} />
      <Route path="/boards/:boardId" element={<ProtectedRoute><BoardDetailPage /></ProtectedRoute>} />
      <Route path="/onboarding/mode" element={<ProtectedRoute><OnboardingMode /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
      <Route path="/features" element={<ProtectedRoute><AllFeaturesPage /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ProposalsInboxPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
      <Route path="/settings/privacy" element={<ProtectedRoute><SettingsPrivacy /></ProtectedRoute>} />
      <Route path="/settings/data" element={<ProtectedRoute><SettingsData /></ProtectedRoute>} />
      <Route path="/settings/style-preferences" element={<ProtectedRoute><StylePreferences /></ProtectedRoute>} />
      <Route path="/sharing" element={<ProtectedRoute><SharingPage /></ProtectedRoute>} />
      <Route path="/style-me" element={<ProtectedRoute><StyleMeComposerPage /></ProtectedRoute>} />
      
      {/* Event Routes */}
      <Route path="/events" element={<ProtectedRoute><EventsListPage /></ProtectedRoute>} />
      <Route path="/events/new" element={<ProtectedRoute><EventsListPage /></ProtectedRoute>} />
      <Route path="/events/:event_id" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/:event_id/board" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/:event_id/looks" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/:event_id/chat" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/:event_id/participants" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/:event_id/settings" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />
      <Route path="/events/join/:code" element={<EventInviteAcceptPage />} />

      {/* Profile/Social Routes */}
      <Route path="/u/:handle" element={<ProtectedRoute><PublicProfilePage /></ProtectedRoute>} />

      {/* Founder Console — isolated back-office shell. Guards itself
          (login redirect + team-only screen); NEVER renders consumer nav. */}
      <Route path="/founder/*" element={<ErrorBoundary variant="founder"><FounderConsole /></ErrorBoundary>} />

      {/* Legacy admin URLs → Founder Console equivalents. */}
      <Route path="/admin/*" element={<LegacyAdminRedirect />} />

      {/* Payments user-facing routes (gated by payments_v1 feature flag at backend) */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/account/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/ai-disclosure" element={<AiDisclosure />} />
      {/* Builder 2 — Legal aliases + Community Guidelines / Beta Notice / Affiliate Disclosure. */}
      <Route path="/legal/terms" element={<TermsOfUse />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/community-guidelines" element={<CommunityGuidelines />} />
      <Route path="/legal/ai-disclaimer" element={<AiDisclosure />} />
      <Route path="/legal/beta-notice" element={<BetaNotice />} />
      <Route path="/legal/affiliate-disclosure" element={<AffiliateDisclosure />} />
      <Route path="/legal/accept" element={<ProtectedRoute skipLegalGate><LegalGate /></ProtectedRoute>} />
      {/* F-002 fix — search results in /settings link to /data-deletion and
          /credits; bind them explicitly so those links resolve instead of 404. */}
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/credits" element={<ImageCredits />} />
      
      <Route path="/invite/:code" element={<InviteLandingPage />} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      {/* Codex Phase G — explicit catch-all instead of a silent blank screen. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary variant="consumer">
        <AuthProvider>
          <AppConfigProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="bottom-right" theme="light" />
          </AppConfigProvider>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

